import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useSites } from '@/hooks/useSites';
import { invoiceClient } from '@/lib/api/ts-rest';
import { cn } from '@/lib/utils';
import type { Invoice } from '@octave/contracts';
import { Building2, Check, FileText, Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface UpdateInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  onUpdateSuccess?: () => void;
}

export function UpdateInvoiceDialog({
  open,
  onOpenChange,
  invoice,
  onUpdateSuccess,
}: UpdateInvoiceDialogProps) {
  const { data: organization } = useCurrentOrganization();
  const { data: sites = [] } = useSites(organization?.id);

  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [reference, setReference] = useState('');
  const [type, setType] = useState<'PHONE_PLAN' | 'EQUIPMENT'>('PHONE_PLAN');
  const [date, setDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');

  const filteredSites = useMemo(() => {
    if (!siteSearch.trim()) return sites;
    const q = siteSearch.toLowerCase();
    return sites.filter((s) => s.name.toLowerCase().includes(q));
  }, [sites, siteSearch]);

  useEffect(() => {
    if (invoice) {
      setReference(invoice.reference);
      setType(invoice.type ?? 'PHONE_PLAN');
      setDate(new Date(invoice.date).toISOString().split('T')[0]);
      setSelectedSiteIds(invoice.sites.map((site) => site.id));
    }
  }, [invoice]);

  const handleSiteToggle = (siteId: string) => {
    setSelectedSiteIds((prev) =>
      prev.includes(siteId)
        ? prev.filter((id) => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleUpdate = async () => {
    if (!reference.trim()) {
      toast.error('Veuillez saisir une référence');
      return;
    }
    if (!organization || !invoice) {
      toast.error('Données manquantes');
      return;
    }

    setIsUpdating(true);
    try {
      const res = await invoiceClient.updateInvoice({
        params: { organizationId: organization.id, invoiceId: invoice.id },
        body: {
          reference: reference.trim(),
          type,
          date: new Date(date).toISOString(),
          siteIds: selectedSiteIds,
        },
      });
      if (res.status !== 200) throw new Error('Update failed');
      toast.success('Facture mise à jour avec succès');
      onUpdateSuccess?.();
      handleClose();
    } catch {
      toast.error('Erreur lors de la mise à jour de la facture');
    } finally {
      setIsUpdating(false);
    }
  };

  const resetForm = () => {
    setSelectedSiteIds([]);
    setReference('');
    setType('PHONE_PLAN');
    setDate('');
    setSiteSearch('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="text-lg font-bold text-slate-900">
            Modifier la facture
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Modifiez les informations de la facture
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Current file (read-only) */}
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100">
              <FileText className="h-4 w-4 text-slate-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-700 truncate">
                {invoice.reference}.pdf
              </p>
              <p className="text-[10px] text-slate-400">
                Le fichier ne peut pas être modifié
              </p>
            </div>
          </div>

          {/* Reference + Type in row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">
                Référence
              </Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="INV-2024-001"
                disabled={isUpdating}
                className="bg-white border-slate-200 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">Type</Label>
              <Select
                value={type}
                onValueChange={(value: 'PHONE_PLAN' | 'EQUIPMENT') =>
                  setType(value)
                }
                disabled={isUpdating}
              >
                <SelectTrigger className="bg-white border-slate-200 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PHONE_PLAN">Forfait mobile</SelectItem>
                  <SelectItem value="EQUIPMENT">Équipement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-500">
              Date de la facture
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={isUpdating}
              className="bg-white border-slate-200 text-sm"
            />
          </div>

          {/* Sites selection */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-slate-500">
                Centres de facturation
              </Label>
              {selectedSiteIds.length > 0 && (
                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                  {selectedSiteIds.length} sélectionné
                  {selectedSiteIds.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {sites.length > 5 && (
                <div className="relative border-b border-slate-100">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={siteSearch}
                    onChange={(e) => setSiteSearch(e.target.value)}
                    placeholder="Rechercher un site..."
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-transparent outline-none placeholder:text-slate-400"
                    disabled={isUpdating}
                  />
                </div>
              )}

              <div className="max-h-40 overflow-y-auto">
                {filteredSites.length === 0 ? (
                  <div className="flex flex-col items-center py-6 text-slate-400">
                    <Building2 className="h-5 w-5 mb-1.5 text-slate-300" />
                    <p className="text-xs">
                      {sites.length === 0
                        ? 'Aucun site disponible'
                        : 'Aucun résultat'}
                    </p>
                  </div>
                ) : (
                  filteredSites.map((site) => {
                    const isSelected = selectedSiteIds.includes(site.id);
                    return (
                      <button
                        key={site.id}
                        type="button"
                        onClick={() => handleSiteToggle(site.id)}
                        disabled={isUpdating}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors',
                          isSelected ? 'bg-emerald-50' : 'hover:bg-slate-50'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-all',
                            isSelected
                              ? 'border-emerald-500 bg-emerald-500'
                              : 'border-slate-300 bg-white'
                          )}
                        >
                          {isSelected && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span
                          className={cn(
                            'text-sm truncate',
                            isSelected
                              ? 'text-emerald-900 font-medium'
                              : 'text-slate-700'
                          )}
                        >
                          {site.name}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              {filteredSites.length > 1 && (
                <div className="border-t border-slate-100 px-3 py-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedSiteIds(filteredSites.map((s) => s.id))
                    }
                    disabled={isUpdating}
                    className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Tout sélectionner
                  </button>
                  {selectedSiteIds.length > 0 && (
                    <>
                      <span className="text-xs text-slate-300">·</span>
                      <button
                        type="button"
                        onClick={() => setSelectedSiteIds([])}
                        disabled={isUpdating}
                        className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        Tout désélectionner
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-3 shrink-0 border-t border-slate-100 gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUpdating}
            className="rounded-full border-slate-200 text-slate-600"
          >
            Annuler
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={!reference.trim() || isUpdating}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all"
          >
            {isUpdating ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Mise à jour...
              </span>
            ) : (
              'Mettre à jour'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
